import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { GuardaArchivos } from "@/components/common/GuardaArchivos";
import { Guard, inicioPara, ROL_CARGADOR_LISTAS } from "@/components/common/GuardRol";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute, AppLayout } from "@/components/layout/AppLayout";
// MODO DEMO (sep-2026): inerte en producción (DEMO_ACTIVO === false → no renderiza nada).
import { DEMO_ACTIVO } from "@/demo/modo";
import { FranjaDemo, PieDemo } from "@/demo/Franja";
import { LimiteDeErrorDemo } from "@/demo/LimiteDeError";
import { NoEncontrado } from "@/demo/NoEncontrado";
import { PREFIJO_DEMO, rutaAutorizada } from "@/demo/ruta";

import Login from "@/pages/Login";
import RegisterWizard from "@/pages/RegisterWizard";
import Dashboard from "@/pages/Dashboard";
import GenericResourcePage from "@/pages/GenericResourcePage";
import Simulador from "@/pages/Simulador";
import Cartera from "@/pages/Cartera";
import Pld from "@/pages/Pld";
import Condusef from "@/pages/Condusef";
import Calendario from "@/pages/Calendario";
import Integraciones from "@/pages/Integraciones";
import ConfiguracionNormativa from "@/pages/ConfiguracionNormativa";
import ConfiguracionEntidad from "@/pages/ConfiguracionEntidad";
import Capacitacion from "@/pages/Capacitacion";
import CapacitacionAdmin from "@/pages/CapacitacionAdmin";
import AuditoriaFelvoraAdmin from "@/pages/AuditoriaFelvoraAdmin";
import OtrasObligaciones from "@/pages/OtrasObligaciones";
import Proximamente from "@/pages/Proximamente";
import Facturacion from "@/pages/Facturacion";
import AdminSaas from "@/pages/AdminSaas";
import BarridoDiarioAdmin from "@/pages/BarridoDiarioAdmin";
import RiesgoPlataforma from "@/pages/RiesgoPlataforma";
import CentroListas from "@/pages/CentroListas";
import ListaVigilancia from "@/pages/ListaVigilancia";
import ProveedorListas from "@/pages/ProveedorListas";
import EstadoScreening from "@/pages/EstadoScreening";
import GestionPlanes from "@/pages/GestionPlanes";
import CodigosCortesia from "@/pages/CodigosCortesia";
import AdminUmbrales from "@/pages/AdminUmbrales";
import EntidadDetalle from "@/pages/EntidadDetalle";
import Solicitudes from "@/pages/Solicitudes";
import OnboardingPending from "@/pages/OnboardingPending";
import DocumentosLegales from "@/pages/DocumentosLegales";
import TerminosYCondiciones from "@/pages/TerminosYCondiciones";
import Auditoria from "@/pages/Auditoria";
import UsuariosRoles from "@/pages/UsuariosRoles";
import CentroIntegraciones from "@/pages/CentroIntegraciones";
import Originacion from "@/pages/Originacion";
import ComiteCredito from "@/pages/ComiteCredito";
import MigracionCartera from "@/pages/MigracionCartera";
import Cobranza from "@/pages/Cobranza";
import MesaControl from "@/pages/MesaControl";
import ContabilidadFiscal from "@/pages/ContabilidadFiscal";
import ReportesFiscales from "@/pages/ReportesFiscales";
import EstadosFinancieros from "@/pages/EstadosFinancieros";
import CFDIsofom from "@/pages/CFDIsofom";
import TesoreriaPanel from "@/pages/TesoreriaPanel";
import SociosYAportaciones from "@/pages/SociosYAportaciones";
import FondeoRecibido from "@/pages/FondeoRecibido";
import GastosOperativos from "@/pages/GastosOperativos";
import AvFichaCliente from "@/pages/AvFichaCliente";
import Prospeccion from "@/pages/Prospeccion";
import LeadsCampo from "@/pages/LeadsCampo";
import Cliente360 from "@/pages/Cliente360";
import CanalDenuncia from "@/pages/CanalDenuncia";
import Fideicomisos from "@/pages/Fideicomisos";
import FideicomisoDetalle from "@/pages/FideicomisoDetalle";
import FideicomisosConfig from "@/pages/FideicomisosConfig";
import Lineas from "@/pages/Lineas";
import LineaDetalle from "@/pages/LineaDetalle";
import Landing from "@/pages/Landing";
import DefinirPassword from "@/pages/DefinirPassword";
import RecuperarPassword from "@/pages/RecuperarPassword";
import VerificarCorreo from "@/pages/VerificarCorreo";
import VerificarCorreoPendiente from "@/pages/VerificarCorreoPendiente";
import AvisoPrivacidad from "@/pages/AvisoPrivacidad";
// R7 F2 — Línea Inmobiliaria (rutas aditivas, gateadas por giro en el backend).
import InicioInmo from "@/pages/inmobiliaria/InicioInmo";
import CumplimientoInmo from "@/pages/inmobiliaria/CumplimientoInmo";
import ConfiguracionInmo from "@/pages/inmobiliaria/ConfiguracionInmo";
import Inmuebles from "@/pages/inmobiliaria/Inmuebles";
import Asesores from "@/pages/inmobiliaria/Asesores";
import Operaciones from "@/pages/inmobiliaria/Operaciones";
import Avisos from "@/pages/inmobiliaria/Avisos";
import EbrInmobiliaria from "@/pages/inmobiliaria/EbrInmobiliaria";
import Arrendamientos from "@/pages/inmobiliaria/Arrendamientos";
import CobranzaRentas from "@/pages/inmobiliaria/CobranzaRentas";
import Egresos from "@/pages/inmobiliaria/Egresos";
import EstadosCuenta from "@/pages/inmobiliaria/EstadosCuenta";
import Comisiones from "@/pages/inmobiliaria/Comisiones";
import Reportes from "@/pages/inmobiliaria/Reportes";

function RootRoute() {
  const { user } = useAuth();
  // DEMO (sep-2026 · instrucción del PO): la demostración NO tiene pantalla de acceso.
  // La raíz entra DIRECTO al tablero con la sesión ficticia ya iniciada.
  if (DEMO_ACTIVO) return <Navigate to="/dashboard" replace />;
  if (user === null) return null;
  if (user) return <Navigate to={inicioPara(user.role)} replace />;
  return <Landing />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  // DEMO: /login y /registro se retiran de la demostración y mandan al tablero.
  if (DEMO_ACTIVO) return <Navigate to="/dashboard" replace />;
  if (user === null) return null;
  if (user) return <Navigate to={inicioPara(user.role)} replace />;
  return children;
}

// DEMO: red de seguridad para que un fixture incompleto no deje la pantalla en blanco
// durante una demostración en vivo. En producción `Envoltura` es un paso-través.
const PasoTraves = ({ children }) => children;
const Envoltura = DEMO_ACTIVO ? LimiteDeErrorDemo : PasoTraves;

// DEMO: pantallas que NO forman parte de la demostración (alta de entidades, recuperación
// de contraseña, panel de plataforma, facturación y configuración de credenciales). En vez
// de pedir datos o mostrar una pantalla vacía, llevan al tablero. Nada queda "bloqueado".
const soloProduccion = (el) => (DEMO_ACTIVO ? <Navigate to="/dashboard" replace /> : el);

function App() {
  // DEMO: la demostración se sirve ÚNICAMENTE bajo la ruta secreta (`DEMO_RUTA`). Cualquier
  // otra dirección —incluida la raíz— responde un 404 pelado, sin rastro del producto.
  // En producción esta guarda no existe (DEMO_ACTIVO === false).
  if (DEMO_ACTIVO && !rutaAutorizada(window.location.pathname)) return <NoEncontrado />;
  return (
    <div className="App">
      {DEMO_ACTIVO && <FranjaDemo />}
      <BrowserRouter basename={DEMO_ACTIVO ? PREFIJO_DEMO : undefined}>
        <AuthProvider>
          <TooltipProvider delayDuration={200} skipDelayDuration={100}>
          <Envoltura>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/registro" element={<PublicOnly><RegisterWizard /></PublicOnly>} />
            <Route path="/recuperar" element={soloProduccion(<RecuperarPassword />)} />
            <Route path="/activar" element={soloProduccion(<DefinirPassword modo="invite" />)} />
            <Route path="/restablecer" element={soloProduccion(<DefinirPassword modo="reset" />)} />
            <Route path="/verificar" element={soloProduccion(<VerificarCorreo />)} />
            <Route path="/verificar-correo-pendiente" element={soloProduccion(<VerificarCorreoPendiente />)} />
            {/* Aviso de privacidad integral y Términos y Condiciones (oct-2026, texto legal
                de Servicios Legales y Fiscales BLC, S.C.). Públicos, sin sesión, desde login y
                registro; versión 2026-10-16 (ver lib/legal.js). */}
            <Route path="/aviso-privacidad" element={<AvisoPrivacidad />} />
            <Route path="/terminos-y-condiciones" element={<TerminosYCondiciones />} />
            <Route path="/" element={<RootRoute />} />

            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Guard prohibidos={[ROL_CARGADOR_LISTAS]}><Dashboard /></Guard>} />
              <Route path="/sofom/simulador" element={<Simulador />} />
              <Route path="/sofom/cartera" element={<Cartera />} />
              <Route path="/sofom/cobranza" element={<Cobranza />} />
              <Route path="/mesa-control" element={<MesaControl />} />
              <Route path="/fiscal/catalogo" element={<ContabilidadFiscal />} />
              <Route path="/fiscal/config" element={<ContabilidadFiscal />} />
              <Route path="/fiscal/estados" element={<EstadosFinancieros />} />
              <Route path="/fiscal/reportes" element={<ReportesFiscales />} />
              <Route path="/sofom/cfdi" element={<CFDIsofom />} />
              <Route path="/finanzas/tesoreria" element={<TesoreriaPanel />} />
              <Route path="/finanzas/socios" element={<SociosYAportaciones />} />
              <Route path="/finanzas/fondeo" element={<FondeoRecibido />} />
              <Route path="/finanzas/gastos" element={<GastosOperativos />} />
              <Route path="/prospeccion" element={<Prospeccion />} />
              <Route path="/leads-bandeja" element={<LeadsCampo />} />
              <Route path="/sofom/originacion" element={<Originacion />} />
              <Route path="/comite-credito" element={<ComiteCredito />} />
              <Route path="/migracion-cartera" element={<MigracionCartera />} />
              <Route path="/centro-integraciones" element={soloProduccion(<CentroIntegraciones />)} />
              <Route path="/clientes-360" element={<Cliente360 />} />
              {/* Ficha del cliente de ACTIVIDAD VULNERABLE. Pantalla propia del vertical:
                  no comparte nada con la ficha 360 de SOFOM. El candado real es el backend
                  (403 por giro y por rol en `GET /api/av/clientes/{id}/ficha`). */}
              <Route path="/av/clientes/:clienteId/ficha" element={<AvFichaCliente />} />
              <Route path="/pld" element={<Pld />} />
              <Route path="/canal-denuncia" element={<Guard prohibidos={[ROL_CARGADOR_LISTAS]}><CanalDenuncia /></Guard>} />
              <Route path="/fideicomisos" element={<Fideicomisos />} />
              <Route path="/fideicomisos/configuracion" element={<FideicomisosConfig />} />
              <Route path="/fideicomisos/:id" element={<FideicomisoDetalle />} />
              <Route path="/lineas" element={<Lineas />} />
              <Route path="/lineas/:id" element={<LineaDetalle />} />
              <Route path="/condusef" element={<Condusef />} />
              <Route path="/calendario" element={<Calendario />} />
              <Route path="/integraciones" element={soloProduccion(<Integraciones />)} />
              <Route path="/config-normativa" element={soloProduccion(<ConfiguracionNormativa />)} />
              <Route path="/proximamente" element={soloProduccion(<Proximamente />)} />
              <Route path="/facturacion" element={soloProduccion(<Facturacion />)} />
              <Route path="/admin-saas" element={soloProduccion(<AdminSaas />)} />
              {/* sep-2026 · Las DOS pantallas del rol de plataforma «Cargador de Listas».
                  Guard de rol en la ruta + candado real en el backend (lista blanca de
                  `rol_cargador_listas.py`). */}
              <Route path="/admin-barrido" element={<Guard permitidos={["super_admin", ROL_CARGADOR_LISTAS]}>{soloProduccion(<BarridoDiarioAdmin />)}</Guard>} />
              <Route path="/admin-riesgo" element={soloProduccion(<RiesgoPlataforma />)} />
              <Route path="/admin-listas" element={<Guard permitidos={["super_admin", ROL_CARGADOR_LISTAS]}>{soloProduccion(<CentroListas />)}</Guard>} />
              <Route path="/admin-vigilancia" element={soloProduccion(<ListaVigilancia />)} />
              <Route path="/admin-proveedor-listas" element={soloProduccion(<ProveedorListas />)} />
              {/* oct-2026 · Panel de plataforma: estado REAL del cotejo externo y de las
                  listas globales que se leen en cada alta. Guard de rol en la ruta + 403 en
                  el backend (`/screening/estado-cotejo-externo` solo super_admin). */}
              <Route path="/admin-estado-screening" element={<Guard permitidos={["super_admin"]}>{soloProduccion(<EstadoScreening />)}</Guard>} />
              <Route path="/admin-planes" element={soloProduccion(<GestionPlanes />)} />
              <Route path="/admin-codigos" element={soloProduccion(<CodigosCortesia />)} />
              <Route path="/admin-umbrales" element={soloProduccion(<AdminUmbrales />)} />
              <Route path="/entidades/:id" element={soloProduccion(<EntidadDetalle />)} />
              <Route path="/admin-solicitudes" element={soloProduccion(<Solicitudes />)} />
              <Route path="/onboarding" element={<OnboardingPending />} />
              <Route path="/documentos-legales" element={<DocumentosLegales />} />
              <Route path="/configuracion-entidad" element={<ConfiguracionEntidad />} />
              <Route path="/capacitacion" element={<Capacitacion />} />
              <Route path="/capacitacion-admin" element={soloProduccion(<CapacitacionAdmin />)} />
              <Route path="/auditoria-felvora-admin" element={soloProduccion(<AuditoriaFelvoraAdmin />)} />
              <Route path="/otras-obligaciones" element={<OtrasObligaciones />} />
              <Route path="/auditoria" element={<Auditoria />} />
              <Route path="/usuarios" element={<UsuariosRoles />} />
              {/* R7 F2 — Línea Inmobiliaria */}
              <Route path="/inmobiliaria/inicio" element={<InicioInmo />} />
              <Route path="/inmobiliaria/inmuebles" element={<Inmuebles />} />
              <Route path="/inmobiliaria/asesores" element={<Asesores />} />
              <Route path="/inmobiliaria/operaciones" element={<Operaciones />} />
              <Route path="/inmobiliaria/avisos" element={<Avisos />} />
              <Route path="/inmobiliaria/ebr" element={<EbrInmobiliaria />} />
              <Route path="/inmobiliaria/arrendamientos" element={<Arrendamientos />} />
              <Route path="/inmobiliaria/cobranza" element={<CobranzaRentas />} />
              <Route path="/inmobiliaria/egresos" element={<Egresos />} />
              <Route path="/inmobiliaria/estados-cuenta" element={<EstadosCuenta />} />
              <Route path="/inmobiliaria/comisiones" element={<Comisiones />} />
              <Route path="/inmobiliaria/reportes" element={<Reportes />} />
              {/* feb-2026 · iter 07-26 · Fase PLD puro: 2 pantallas wrapper con tabs internos */}
              <Route path="/inmobiliaria/cumplimiento" element={<CumplimientoInmo />} />
              <Route path="/inmobiliaria/configuracion" element={<ConfiguracionInmo />} />
              <Route path="/inmobiliaria" element={<InicioInmo />} />
              <Route path="/r/:slug" element={<GenericResourcePage />} />
            </Route>

            {/* DEMO: atajos por si alguien teclea la ruta "corta" en una demostración. */}
            {DEMO_ACTIVO && <Route path="/cartera" element={<Navigate to="/sofom/cartera" replace />} />}
            {DEMO_ACTIVO && <Route path="/cobranza" element={<Navigate to="/sofom/cobranza" replace />} />}
            {DEMO_ACTIVO && <Route path="/clientes" element={<Navigate to="/r/clientes" replace />} />}

            <Route path="*" element={<Navigate to={DEMO_ACTIVO ? "/dashboard" : "/"} replace />} />
          </Routes>
          </Envoltura>
          <Toaster position="top-right" richColors />
          <GuardaArchivos />
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
      {DEMO_ACTIVO && <PieDemo />}
    </div>
  );
}

export default App;
